import type { TokenPair } from '@rfleet/shared';

export interface ApiErrorBody {
  code: string;
  messageAr: string;
  messageEn: string;
  details?: unknown;
  requestId?: string;
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: ApiErrorBody,
  ) {
    super(body.messageEn);
  }
  get localized(): string {
    return document.documentElement.lang === 'en' ? this.body.messageEn : this.body.messageAr;
  }
}

const BASE = (import.meta.env['VITE_API_URL'] as string | undefined) ?? '';
const REFRESH_KEY = 'rfleet.refresh';

let accessToken: string | null = null;
let refreshing: Promise<boolean> | null = null;
let onSessionLost: (() => void) | null = null;

export const tokenStore = {
  setAccess(t: string | null) {
    accessToken = t;
  },
  peekAccess(): string | null {
    return accessToken;
  },
  getRefresh(): string | null {
    try {
      return localStorage.getItem(REFRESH_KEY);
    } catch {
      return null;
    }
  },
  setRefresh(t: string | null) {
    try {
      if (t) localStorage.setItem(REFRESH_KEY, t);
      else localStorage.removeItem(REFRESH_KEY);
    } catch {
      /* ignore */
    }
  },
  setSessionLostHandler(fn: (() => void) | null) {
    onSessionLost = fn;
  },
  clear() {
    accessToken = null;
    tokenStore.setRefresh(null);
  },
};

export interface ApiOptions extends Omit<RequestInit, 'body'> {
  json?: unknown;
  body?: BodyInit | null;
  /** Skip the automatic refresh-and-retry on 401. */
  noRetry?: boolean;
  /** Super-admin support mode: act inside a tenant. */
  tenantId?: string;
}

async function rawFetch(path: string, init: ApiOptions): Promise<Response> {
  const headers = new Headers(init.headers);
  headers.set('accept', 'application/json');
  headers.set('accept-language', document.documentElement.lang || 'ar');
  if (init.json !== undefined) headers.set('content-type', 'application/json');
  if (accessToken) headers.set('authorization', `Bearer ${accessToken}`);
  if (init.tenantId) headers.set('x-tenant-id', init.tenantId);
  return fetch(`${BASE}/api/v1${path}`, { ...init, headers, body: init.json !== undefined ? JSON.stringify(init.json) : init.body });
}

/** Refreshes the access token once; concurrent callers share the same promise. */
export async function refreshSession(): Promise<boolean> {
  if (refreshing) return refreshing;
  const refreshToken = tokenStore.getRefresh();
  if (!refreshToken) return false;
  refreshing = (async () => {
    try {
      const res = await fetch(`${BASE}/api/v1/auth/refresh`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });
      if (!res.ok) {
        tokenStore.clear();
        onSessionLost?.();
        return false;
      }
      const { data } = (await res.json()) as { data: TokenPair };
      tokenStore.setAccess(data.accessToken);
      tokenStore.setRefresh(data.refreshToken);
      return true;
    } catch {
      return false;
    } finally {
      refreshing = null;
    }
  })();
  return refreshing;
}

export async function api<T>(path: string, init: ApiOptions = {}): Promise<T> {
  let res = await rawFetch(path, init);
  if (res.status === 401 && !init.noRetry && (await refreshSession())) {
    res = await rawFetch(path, init);
  }
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  const parsed = text ? (JSON.parse(text) as { data?: T; meta?: unknown } & ApiErrorBody) : ({} as never);
  if (!res.ok) {
    if (res.status === 401) onSessionLost?.();
    throw new ApiError(res.status, parsed);
  }
  return (parsed.meta !== undefined ? parsed : parsed.data) as T;
}

export interface Paginated<T> {
  data: T[];
  meta: { page: number; pageSize: number; total: number };
}

export const qs = (params: Record<string, string | number | boolean | undefined | null>): string => {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== null && v !== '') p.set(k, String(v));
  const s = p.toString();
  return s ? `?${s}` : '';
};

/** Authenticated fetch that returns the raw body (HTML print views, exports). */
export async function apiText(path: string): Promise<string> {
  let res = await rawFetch(path, {});
  if (res.status === 401 && (await refreshSession())) res = await rawFetch(path, {});
  if (!res.ok) {
    const text = await res.text();
    throw new ApiError(res.status, text ? (JSON.parse(text) as ApiErrorBody) : ({} as ApiErrorBody));
  }
  return res.text();
}
